import {debounce} from 'lodash-es';

import {AIChatPanelExpandSource} from '@/utils/document/aigc';
import * as store from '@/store';

import {feLogger} from './index';

enum RepType {
    Click = 'click',
    Show = 'show',
}

export const AigcLogIdMap = {
    /**
     * 右侧超级助理「文案修改，原来是智能洞察」埋点
     */
    clickEntry: '80001.2.162', // 点击入口
    completeConversation: '80001.2.163', // 完成对话
    showAbstract: '80001.2.164', // 摘要曝光量
    generateAction: '80001.2.165', // 生成耗时相关埋点
    clickLike: '80001.2.170', // 点赞
    clickDisLike: '80001.2.171', // 点踩
    clickCopy: '80001.2.172', // 点击复制（包括一键复制 和 代码块复制）
    clickRegenerate: '80001.2.173', // 点击重新生成
    clickRecommend: '80001.2.174', // 点击推荐问题
    showExistAbstract: '80001.2.175', // 洞察打开时有摘要
    showContentCount: '80001.2.183', // 洞察打开次数
    showExistQuestion: '80001.2.184', // 洞察打开时有推荐问题
    clickClosePanel: '80001.2.196', // 点击关闭面板
    /**
     * hover 引用文档出摘要埋点
     */
    hoverShowAbstract: '80001.2.176', // hover 展示摘要
    hoverShowScreenShot: '80001.2.177', // hover 展示截图
    hoverShowApplyPermission: '80001.2.185', // hover 展示权限申请
    clickAbstract: '80001.2.178', // 点击摘要
    hoverClickLike: '80001.2.179', // hover 点赞
    hoverClickDisLike: '80001.2.180', // hover 点踩
    generateAbstractTime: '80001.2.181', // 摘要生成耗时
    viewAbstractTime: '80001.2.182', // 浏览摘要时长
    mentionDocShowScreen: '80001.2.188', // 引用文档展示在屏幕中
    clickAbstractInRuLiu: '80001.7.17', // 如流 IM 卡片，点击摘要
    /**
     * 空文档大纲触发超级助理推荐文档
     */
    recommendDoc: '80001.1.128', // 生成推荐文档
    recommendDocAction: '80001.1.129', // 推荐文档交互行为
    showExistRecommendDocCard: '80001.1.127', // 洞察打开时逐条上报推荐文档卡片

    /**
     * 智能评分
     */
    gradeGenerate: '80001.2.223',
    gradeDocEdit: '80001.2.227',
    gradeSuggest: '80001.2.228',
    gradeAdoptClick: '80001.2.224',
    gradeIgnoreClick: '80001.2.225',
    gradeCardShow: '80001.2.237',
    gradeCardExpandBtnClick: '80001.2.238',
    agentAvatarClick: '80001.2.236',
    agentAvatarTipsShow: '80001.2.239',
    gradeAdoptBtnClick: '80001.2.244',
    gradeIgnoreBtnClick: '80001.1.216',
    gradeLinkClick: '80001.2.245',
    agentAvatarTipsCloseBtnClick: '80001.2.246',
    gradeLikeClick: '80001.1.168',
    gradeDislikeClick: '80001.1.169',
    gradeCopyClick: '80001.1.170',
    // 智能评分点击用户点击【点击开始检测】/【点击重新检测】/【重新检测】的次数
    startAIGCAgent: '80001.2.295',
    /**
     * 标题推荐
     */
    // 曝光
    titleRecommendBtnExposure: '80001.8.143',
    // 点击推荐按钮
    titleRecommendBtnClick: '80001.8.144',
    // 点击放弃按钮
    titleRevokeBtnClick: '80001.8.145',
};

const report = (reportId: string, data: any = {}, rep_type = RepType.Click) => {
    feLogger({
        rep_id: reportId,
        rep_type,
        rep_params: {...data},
    });
};

export const reportClickEntry = (data: {
    position: 'entry' | 'tab';
    pageId: string;
    documentMode: string;
}) => {
    report(AigcLogIdMap.clickEntry, data);
};

export const reportCompleteConversation = (data: {
    pageId: string;
}) => {
    report(AigcLogIdMap.completeConversation, data);
};

export const reportClickRecommend = (data: {
    question: string;
}) => {
    report(AigcLogIdMap.clickRecommend, data);
};

export const reportGenerateAction = (data: {
    question: string;
    delay: number;
    sessionId: string;
    action: 'first' | 'complete';
}) => {
    report(AigcLogIdMap.generateAction, data);
};

export const reportClickLike = (data: {
    question: string;
    answer: string;
}) => {
    report(AigcLogIdMap.clickLike, data);
};

export const reportClickDisLike = (data: {
    question: string;
    answer: string;
}) => {
    report(AigcLogIdMap.clickDisLike, data);
};

export const reportClickCopy = (data: {
    position: 'complete' | 'code';
}) => {
    report(AigcLogIdMap.clickCopy, data);
};

export const reportClickRegenerate = () => {
    report(AigcLogIdMap.clickRegenerate, {});
};

export const reportShowAbstract = (data: {
    pageId: string;
    type: 'observe' | 'exist';
    abstractNum?: number;
}) => {
    report(AigcLogIdMap.showAbstract, data, RepType.Show);
};

export const reportShowExistAbstract = (data: {
    pageId: string;
    visible?: boolean; // 判断摘要是否可见，Dom 在可视区域 或 摘要是记录最后一条均视为可见
    panelExpandSource?: AIChatPanelExpandSource;
}) => {
    report(AigcLogIdMap.showExistAbstract, data, RepType.Show);
};

export const reportShowContentCount = (data: {
    pageId: string;
    panelExpandSource: AIChatPanelExpandSource;
}) => {
    report(AigcLogIdMap.showContentCount, data, RepType.Click);
};

export const reportShowExistQuestion = (data: {
    pageId: string;
    panelExpandSource: AIChatPanelExpandSource;
}) => {
    report(AigcLogIdMap.showExistQuestion, data, RepType.Show);
};

export const reportHoverShowAbstract = (data: {
    mentionDocId: string;
    isHighLight: boolean;
    like: string;
    summaryNum: number;
    isCache: boolean;
}) => {
    report(AigcLogIdMap.hoverShowAbstract, data);
};


export const reportHoverShowScreenShot = (data: {
    mentionDocId: string;
    isCache: boolean;
}) => {
    report(AigcLogIdMap.hoverShowScreenShot, data);
};

export const reportHoverShowApplyPermission = (data: {
    mentionDocId: string;
}) => {
    report(AigcLogIdMap.hoverShowApplyPermission, data);
};

export const reportClickAbstract = (data: {
    mentionDocId: string;
}) => {
    report(AigcLogIdMap.clickAbstract, data);
};

export const reportClickAbstractInRuLiu = (data: {
    docId: string;
}) => {
    report(AigcLogIdMap.clickAbstractInRuLiu, data);
};

export const reportHoverClickLike = (data: {
    mentionDocId: string;
    summary: string;
}) => {
    report(AigcLogIdMap.hoverClickLike, data);
};

export const reportHoverClickDisLike = (data: {
    mentionDocId: string;
    summary: string;
}) => {
    report(AigcLogIdMap.hoverClickDisLike, data);
};

export const reportGenerateAbstractTime = (data: {
    mentionDocId: string;
    delay: number;
}) => {
    report(AigcLogIdMap.generateAbstractTime, data);
};

export const reportViewAbstractTime = (data: {
    mentionDocId: string;
    summary: string;
    time: number;
}) => {
    report(AigcLogIdMap.viewAbstractTime, data);
};

export const reportMentionDocShowScreen = (data: {ids: string[]}) => {
    report(AigcLogIdMap.mentionDocShowScreen, data);
};

export const recordShowMentionDocObserver = {
    observer: null as IntersectionObserver | null,
    reportedMap: {} as {[key in string]: boolean},
    paddingList: [] as string[],
    report: () => {
        if (recordShowMentionDocObserver.paddingList.length) {
            reportMentionDocShowScreen({ids: recordShowMentionDocObserver.paddingList});
            recordShowMentionDocObserver.paddingList = [];
        }
    },
    debounceReport: debounce(() => {
        recordShowMentionDocObserver.report();
    }, 300),
    observe() {
        this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const uuid = (entry.target as HTMLElement).dataset.mentionUuid || '';
                    const type = (entry.target as HTMLElement).dataset.mentionType || '';
                    const docType = (entry.target as HTMLElement).dataset.mentionDocType || '';

                    if (this.reportedMap[uuid]) {
                        return;
                    }

                    if (
                        uuid &&
                        type === 'doc' &&
                        (docType === 'doc' || !docType)
                    ) {
                        this.paddingList.push(uuid);
                        this.debounceReport();

                        this.reportedMap[uuid] = true;
                    }
                }
            });
        }, {threshold: [1]});

        const elList = document.querySelectorAll(
            '.mp-layout-content-container [data-mention-uuid][data-mention-type="doc"]'
        );
        if (elList.length > 0) {
            elList.forEach(el => {
                this.observer && this.observer.observe(el);
            });
        }
    },
    start() {
        this.report();
        this.reportedMap = {};
        this.disconnect();
        this.observe();
    },
    debounceObserve: debounce(() => {
        recordShowMentionDocObserver.disconnect();
        recordShowMentionDocObserver.observe();
    }, 500),
    disconnect() {
        this.observer && this.observer.disconnect();
    },
};

export const reportClickClosePanel = (data: {
    pageId: string;
}) => {
    report(AigcLogIdMap.clickClosePanel, data);
};

export const reportRecommendDoc = (data: {
    status: 0 | 1; // 0: 推荐失败, 1: 推荐成功
    recallDocIds: string; // 推荐系统返回文档ID
    time: number; // 生成耗时
    docId: string;
    msgId: string;
}) => {
    report(AigcLogIdMap.recommendDoc, data);
};

export const reportRecommendDocAction = (data: {
    action: 'click' | 'hover'; // 点击或 hover
    actionDocId: string;
    msgId: string;
    hoverTime?: number; // hover 时长
}) => {
    const {docGuid} = store.getData(store.KEYS.CUR_DOCUMENT) || {};
    report(AigcLogIdMap.recommendDocAction, {
        ...data,
        docId: docGuid,
    });
};

export const reportShowExistRecommendDocCard = (data: {
    docId: string;
    msgIdArr: string[];
    panelExpandSource?: AIChatPanelExpandSource;
}) => {
    (data.msgIdArr || []).slice(0, 4).forEach(msgId => {
        report(AigcLogIdMap.showExistRecommendDocCard, {
            docId: data.docId,
            msgId,
            panelExpandSource: data.panelExpandSource,
        }, RepType.Show);
    });
};

export const reportGradeGenerate = (data: {
    docId: string;
}) => {
    report(AigcLogIdMap.gradeGenerate, data);
};

export const reportGradeDocEdit = (data: {
    docId: string;
    wordCount: number;
}) => {
    report(AigcLogIdMap.gradeDocEdit, data);
};

export const reportGradeSuggest = (data: {
    docId: string;
    id?: string;
    type: string;
    count?: number;
}) => {
    report(AigcLogIdMap.gradeSuggest, data);
};

export const reportGradeAdoptClick = (data: {
    docId: string;
    id: string;
    type: string;
    originText: string;
    fixedText: string;
}) => {
    report(AigcLogIdMap.gradeAdoptClick, data);
};

export const reportGradeIgnoreClick = (data: {
    docId: string;
    id: string;
    type: string;
    originText: string;
    fixedText: string;
}) => {
    report(AigcLogIdMap.gradeIgnoreClick, data);
};

export const reportGradeCardShow = (data: {
    docId: string;
    expand: 0 | 1;
    classify: string;
}) => {
    report(AigcLogIdMap.gradeCardShow, data);
};

export const reportGradeCardExpandBtnClick = (data: {
    docId: string;
    expand: 0 | 1;
}) => {
    report(AigcLogIdMap.gradeCardExpandBtnClick, data);
};

export const reportAgentAvatarClick = (data: {
    docId: string;
    target: 'card' | 'bubble';
    hasTips: 0 | 1;
}) => {
    report(AigcLogIdMap.agentAvatarClick, data);
};

export const reportAgentAvatarTipsShow = (data: {
    docId: string;
}) => {
    report(AigcLogIdMap.agentAvatarTipsShow, data);
};

export const reportGradeAdoptBtnClick = (data: {
    docId: string;
    type: string;
    multi: boolean;
}) => {
    report(AigcLogIdMap.gradeAdoptBtnClick, data);
};

export const reportGradeIgnoreBtnClick = (data: {
    docId: string;
    type: string;
    multi: boolean;
}) => {
    report(AigcLogIdMap.gradeIgnoreBtnClick, data);
};

export const reportGradeLinkClick = (data: {
    docId: string;
    // 1: 范文, 2: 专词推荐文档
    type: 1 | 2;
}) => {
    report(AigcLogIdMap.gradeLinkClick, data);
};

export const reportGradeLikeClick = (data: {
    docId: string;
    action: 1 | 0;
}) => {
    report(AigcLogIdMap.gradeLikeClick, data);
};

export const reportGradeDislikeClick = (data: {
    docId: string;
    action: 1 | 0;
}) => {
    report(AigcLogIdMap.gradeDislikeClick, data);
};

export const reportGradeCopyClick = (data: {
    docId: string;
}) => {
    report(AigcLogIdMap.gradeCopyClick, data);
};

export const reportAgentAvatarTipsCloseBtnClick = (data: {
    docId: string;
}) => {
    report(AigcLogIdMap.agentAvatarTipsCloseBtnClick, data);
};


export const reportTitleRecommendBtnExposure = (data: {
    pageId: string;
}) => {
    report(AigcLogIdMap.titleRecommendBtnExposure, data);
};

export const reportTitleRecommendBtnClick = (data: {
    pageId: string;
}) => {
    report(AigcLogIdMap.titleRecommendBtnClick, data);
};

export const reportTitleRevokeBtnClick = (data: {
    pageId: string;
}) => {
    report(AigcLogIdMap.titleRevokeBtnClick, data);
};


// 智能评分点击用户点击【点击开始检测】/【点击重新检测】/【重新检测】的次数
export const clickStartAIGCAgent = (type: 'start' | 'restartWhenUnValid' | 'restartWhenValid') => {
    report(AigcLogIdMap.startAIGCAgent, {
        type,
    });
};
